import {
  fetchReadingState,
  getReadingStateStorageKey,
  selectReadingStateForRestore,
  parseReadingState,
  serializeReadingState,
  type ReadingState,
} from '@narrativebench/editor-core';
import { offlineFetch } from './offline-fetch';

const SERVER_SAVE_DEBOUNCE_MS = 2000;

const readSessionReadingState = (documentId: string): ReadingState | null =>
  typeof sessionStorage !== 'undefined'
    ? parseReadingState(sessionStorage.getItem(getReadingStateStorageKey(documentId)))
    : null;

export const loadReadingStateForDocument = async (
  apiBase: string,
  documentId: string,
  token: string | null
): Promise<ReadingState | null> => {
  const local = readSessionReadingState(documentId);

  if (!token) {
    return local;
  }

  try {
    const remote = await fetchReadingState(apiBase, documentId, token);
    const restored = selectReadingStateForRestore(local, remote);
    if (restored) {
      writeReadingStateToSessionStorage(documentId, restored);
    }
    return restored;
  } catch {
    return local;
  }
};

export const writeReadingStateToSessionStorage = (
  documentId: string,
  state: ReadingState
): void => {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(getReadingStateStorageKey(documentId), serializeReadingState(state));
};

export type DebouncedReadingStateSaver = {
  schedule: (state: ReadingState) => void;
  flush: () => Promise<void>;
  cancel: () => void;
};

export const createDebouncedReadingStateSaver = (
  apiBase: string,
  documentId: string,
  token: string
): DebouncedReadingStateSaver => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let pendingState: ReadingState | null = null;
  let inFlight: Promise<void> | null = null;

  const sendToServer = async (state: ReadingState): Promise<void> => {
    try {
      const response = await offlineFetch(`${apiBase}/api/documents/${documentId}/reading-state`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pos: Math.max(0, Math.round(state.pos)),
          scrollTop: Math.max(0, Math.round(state.scrollTop)),
        }),
      });

      if (!response.ok) {
        return;
      }

      const data = (await response.json()) as { readingState: ReadingState };
      writeReadingStateToSessionStorage(documentId, data.readingState);
      pendingState = null;
    } catch {
      return;
    }
  };

  const flush = async (): Promise<void> => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    if (inFlight) {
      await inFlight;
    }

    if (!pendingState) {
      return;
    }

    const stateToSend = pendingState;
    pendingState = null;
    inFlight = sendToServer(stateToSend).finally(() => {
      inFlight = null;
    });
    await inFlight;
  };

  const schedule = (state: ReadingState): void => {
    pendingState = state;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      timeoutId = null;
      void flush();
    }, SERVER_SAVE_DEBOUNCE_MS);
  };

  const cancel = (): void => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    pendingState = null;
  };

  return { schedule, flush, cancel };
};
